<template>
  <div class="auth-phone">
    <div class="auth-phone__text-content">
      <div class="auth-phone__title">
        <TypographyComp
          v-editor-inline="'Авторизация_заг'"
          preset="heading-m"
        />
      </div>

      <div class="auth-phone__description">
        <TypographyComp
          v-editor-inline="'Авторизация_текст'"
          preset="text-s"
        />
      </div>
    </div>

    <div class="auth-phone__input-wrapper">
      <input
        ref="inputRef"
        :value="phoneValue"
        type="tel"
        :class="authPhoneInputClasses"
        placeholder="+7 (000) 000-00-00"
        @input="handleInput"
        @focus="handleFocus"
      >
    </div>

    <div class="auth-phone__terms-wrapper">
      <CommonCheckbox
        v-model="isTermsAccepted"
        class="auth-phone__checkbox"
      />
      <TypographyComp
        preset="text-xs"
        class="auth-phone__terms"
      >
        Принимаю
        <TypographyComp
          preset="text-xs"
          href="/"
          class="auth-phone__terms--rules"
          @click.prevent="handleRulesClick"
        >
          правила проведения акции
        </TypographyComp>
      </TypographyComp>
    </div>

    <CommonButton
      :disabled="!isValid || !isTermsAccepted || isLoading"
      :is-loading="isLoading"
      @click="handleSubmit"
    >
      <TypographyComp
        v-editor-inline="'Авторизация_кнопка'"
      />
    </CommonButton>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import CommonCheckbox from '@common/components/CommonCheckbox.vue';
import { useAuth } from '@common/composables/useAuth';
import type { AuthStatus } from '@common/types/auth';

interface Props {
  isLoading: boolean;
  authStatus: AuthStatus;
}

const { authStatus } = defineProps<Props>();

const { isPhoneLengthValid, formatPhone } = useAuth();

const emit = defineEmits<{
  submit: [phone: string];
}>();

const phoneValue = ref<string>('');
const hasError = ref<boolean>(false);
const isTermsAccepted = ref<boolean>(false);
const inputRef = ref<HTMLInputElement | null>(null);

const isValid = computed<boolean>(() => isPhoneLengthValid(phoneValue.value));

const authPhoneInputClasses = computed<(string | Record<string, boolean>)[]>(() => [
  'auth-phone__input',
  {
    'auth-phone__input--error': hasError.value,
  },
]);

const handleInput = (event: Event): void => {
  const target = event.target as HTMLInputElement;
  const input = target.value.replace(/\D/g, '');
  phoneValue.value = formatPhone(input);
  hasError.value = false;
};

const handleSubmit = (): void => {
  if (!isValid.value) {
    hasError.value = true;
    return;
  }

  emit('submit', phoneValue.value.replace(/\D/g, ''));
};

const handleFocus = (): void => {
  if (!inputRef.value) return;

  inputRef.value.select();
};

const handleRulesClick = (): void => {
  // Обработчик клика по ссылке правил проведения акции
};

watch(() => authStatus, (newStatus) => {
  if (newStatus === 'error') {
    hasError.value = true;
  }
});

onMounted(() => {
  handleFocus();
});
</script>

<style lang="scss" scoped>
.auth-phone {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 2.8rem;
  max-width: 32rem;
  margin: 0 auto;

  &__text-content {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
  }

  &__title {
    text-align: center;
  }

  &__description {
    text-align: center;
  }

  &__input {
    width: 100%;
    padding: 1.6rem;
    font-size: 2rem;
    font-weight: 700;
    line-height: 115%;
    letter-spacing: -0.08rem;
    border: 0.1rem solid $color-gray;
    border-radius: 0.4rem;
    outline: none;
    transition: border-color 0.3s;

    &-wrapper {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &::placeholder {
      color: $color-gray;
    }

    &--error {
      border-color: $color-sirena-wrong;
      color: $color-sirena-wrong;
    }
  }

  &__terms {
    &-wrapper {
      display: flex;
      align-items: center;
      gap: 0.7rem;
    }

    &--rules {
      text-decoration: underline;
      cursor: pointer;
      color: $color-sirena-main;
    }
  }
}
</style>
